import { cva, type VariantProps } from "@msuite/ui/libs/cva";
import { useTranslation } from "@msuite/ui/localization";
import { Popover } from "@msuite/ui/popover";
import { useQuery } from "@msuite/ui/query";
import { Skeleton } from "@msuite/ui/skeleton";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { cn, responseHandler } from "@msuite/ui/utils";
import type { Database, Nullable, User } from "@msuite/utils/types";
import { Fragment } from "react";
import { UserPopover } from "@/components/popovers/user-popover";
import { client } from "@/hono";
import { UserAvatar } from "./user-avatar";
import { UserCardAbsenceIndicator } from "./user-card-absence-indicator";
import { UserCardContextMenu } from "./user-card-context-menu";

interface BaseProps extends VariantProps<typeof userCard> {
	withStatusIndicator?: boolean;
	absenceTargetDate?: string;
	withAvatar?: boolean;
	withPopover?: boolean;
	render?: (
		user?:
			| Partial<User>
			| {
					createdAt: Date | null;
					email: string | null;
					firstName: string;
					fullName: string | null;
					id: string;
					initials: string | null;
					lastName: string;
					state: Database["public"]["Enums"]["userState"];
					imageUrl: string | null;
			  }
			| null,
	) => React.ReactNode;
}

interface WithId extends BaseProps {
	userId: Nullable<string>;
	user?: never;
}

interface WithUser extends BaseProps {
	user: Partial<User>;
	userId: string;
}

type UserCardProps = WithId | WithUser;

export function UserCard({
	variant,
	fontSize,
	display,
	withStatusIndicator = false,
	withAvatar = true,
	withPopover = true,
	userId,
	user,
	render,
	absenceTargetDate,
}: UserCardProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Query (only if userId is provided and no user object) */
	const { data } = useQuery({
		queryKey: ["users", userId ?? ""],
		queryFn: async () => {
			if (!userId) throw new Error("No userId provided");
			const response = await client.users[":userId"].$get({
				param: { userId },
			});
			// No error toast — same reasoning as `user-avatar.tsx`: an absent row is the users
			// policy withholding a partner's name on purpose, not a failure to report.
			const data = await responseHandler(response, false);
			return data.user;
		},
	});

	/** Source of truth */
	const member = user ?? data ?? null;

	/** CVA */
	const classes = userCard({ variant, fontSize, display });

	if (!member) {
		return <p>{t("users.user_does_not_exist")}</p>;
	}

	if (render) {
		return render(member);
	}

	/** Render */
	return (
		<UserCardContextMenu userId={member.id}>
			<Popover
				render={() => (member.id ? <UserPopover userId={member?.id} /> : <div />)}
				trigger="hover"
				isDisabled={!withPopover}
			>
				<div className={cn("flex-row items-center gap-2.5", classes)}>
					{withAvatar && member.id && <UserAvatar userId={member.id} />}
					{member?.lastName ? (
						<p className="block overflow-ellipsis line-clamp-1 whitespace-nowrap">
							{`${member?.firstName ?? ""} ${member?.lastName ?? ""}`.trim()}
						</p>
					) : (
						<Skeleton placeholder="Markus Mustermann" />
					)}
					{withStatusIndicator && (
						<Fragment>
							<div className="flex-1" />
							<StatusIndicator color={member?.state === "active" ? "emerald" : "gray"} />
						</Fragment>
					)}
					{absenceTargetDate && userId ? (
						<UserCardAbsenceIndicator
							userId={userId}
							targetDate={absenceTargetDate}
						/>
					) : null}
				</div>
			</Popover>
		</UserCardContextMenu>
	);
}

const userCard = cva(["flex"], {
	variants: {
		variant: {
			default: ["card"],
			plain: [],
		},
		fontSize: {
			xs: ["text-xs"],
			sm: ["text-sm"],
			md: ["text-base"],
			lg: ["text-lg"],
		},
		display: {
			inline: ["inline-flex"],
		},
	},
	defaultVariants: {
		variant: "default",
		fontSize: "sm",
	},
});
